import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import CustomButton from "../../components-library/buttons/CustomButton";
import OrderMethodInfo from "../../components-library/checkout/OrderMethodInfo";
import TextArea from "../../components-library/inputs/TextArea";
import TextInput from "../../components-library/inputs/TextInput";
import ProfileManager from "../../services/api/ProfileManager";
import {ButtonTypes, DeliveryAddressDataInterface} from "../../services/exports/Interfaces";
import {actionCreators} from "../../store/actions";
import ReduxActions from "../../store/ReduxActions";
import useAlert from "../../hooks/utility/useAlert";
import {useTranslation} from "react-i18next";
import {StoreInterface} from "../../store/types";
import {DeliveryProvider, ORDER_METHODS} from "../../services/exports/Constants";

interface Props {
  onClickInfo: () => void;
  onContinue: () => void;
}

export default function OrderMethodStep(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:Checkout:OrderMethodStep' });

  const { onClickInfo, onContinue } = props;

  const alert = useAlert();
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const [doorName, setDoorName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [orderInstructions, setOrderInstructions] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setDoorName(profile?.delivery_doorbell_name ?? "");
    setCompanyName(profile?.delivery_company_name ?? "");
    setDeliveryNote(profile?.customer_note ?? "");
  }, [profile]);

  useEffect(() => {
    setOrderInstructions(cached_order?.order_instructions ?? "");
  }, [cached_order?.order_instructions]);

  function getData(): DeliveryAddressDataInterface {
    return {
      delivery_doorbell_name: doorName,
      delivery_company_name: companyName,
      customer_note: deliveryNote,
    };
  }

  async function updateProfile() {
    setLoading(true);
    const { success } = await ProfileManager.putProfile(getData());
    setLoading(false);

    if (success) {
      cacheOrderRedux();

      return onContinue();
    }

    alert.error({
      description: t("error_messages.update_profile_unsuccessful"),
    });
  }

  function cacheOrderRedux() {
    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        delivery_doorbell_name: doorName,
        delivery_company_name: companyName,
        customer_note: deliveryNote,
        order_instructions: orderInstructions,
      })
    );
  }

  function renderContinueButton() {
    return (
      <div className="mt-small">
        <CustomButton
          title={t("custom_button.continue")}
          buttonType={ButtonTypes.secondary}
          loading={loading}
          onClick={updateProfile}
        />
      </div>
    );
  }

  function renderOrderMethodInfo() {
    return (
      <div className="mb-small">
        <OrderMethodInfo onClick={onClickInfo} />
      </div>
    );
  }

  function renderTextInputs() {
    return cached_order?.method === ORDER_METHODS.DELIVERY ? (
      <div>
        {company?.delivery_provider === DeliveryProvider.Standalone && (
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-2 mb-small">
            <TextInput
              type="text"
              placeholder={t("text_inputs.door_name.placeholder")}
              value={doorName}
              onChange={setDoorName}
            />
            <TextInput
              type="text"
              placeholder={t("text_inputs.company_name.placeholder")}
              value={companyName}
              onChange={setCompanyName}
            />
          </div>
        )}
        <TextArea
          placeholder={t("text_inputs.note.placeholder")}
          value={deliveryNote}
          onChange={setDeliveryNote}
          maxLength={80}
        />
      </div>
    ) : undefined;
  }

  function renderOrderInstructions() {
    return (
      <div className="mb-small">
        <div className="mb-small">
          <p className="mini text-brand-text-grey">
            {company?.order_instructions_title}
          </p>
        </div>
        <TextArea
          value={orderInstructions}
          onChange={setOrderInstructions}
          placeholder={t("text_inputs.order_instruction.placeholder")}
          maxLength={160}
        />
      </div>
    );
  }

  return (
    <div>
      {renderOrderMethodInfo()}
      {cached_order?.method === ORDER_METHODS.ROOM_SERVICE && renderOrderInstructions()}
      {renderTextInputs()}
      {renderContinueButton()}
    </div>
  );
}
